"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.defFingerprint = exports.defSetDimensions = exports.defTotals = exports.defSetPostProcessing = exports.defSetExecConfig = exports.defSetSorts = exports.defWithFilters = void 0;
var tslib_1 = require("tslib");
// (C) 2019-2021 GoodData Corporation
var isEmpty_1 = tslib_1.__importDefault(require("lodash/isEmpty"));
var isString_1 = tslib_1.__importDefault(require("lodash/isString"));
var spark_md5_1 = tslib_1.__importDefault(require("spark-md5"));
var ts_invariant_1 = tslib_1.__importDefault(require("ts-invariant"));
var dimension_1 = require("../base/dimension");
var filterMerge_1 = require("../filter/filterMerge");
var fingerprint_1 = require("../measure/fingerprint");
var fingerprints_1 = require("./fingerprints");
var fingerprint_2 = require("../base/fingerprint");
var fingerprint_3 = require("../filter/fingerprint");
/**
 * Creates new execution definition by merging new filters into an existing definition.
 *
 * @param def - existing definition
 * @param filters - array of filters to add to definition
 * @returns always new instance
 * @public
 */
function defWithFilters(def, filters) {
    if (filters === void 0) { filters = []; }
    ts_invariant_1.default(def, "execution definition to add more filters to must be defined");
    if (isEmpty_1.default(filters)) {
        return def;
    }
    return tslib_1.__assign(tslib_1.__assign({}, def), { filters: filterMerge_1.mergeFilters(def.filters, filters) });
}
exports.defWithFilters = defWithFilters;
/**
 * Creates new execution definition by merging new sort items into an existing definition.
 *
 * @param def - existing definition
 * @param sortBy - array of sort items to add to definition
 * @returns always new instance
 * @public
 */
function defSetSorts(def, sortBy) {
    if (sortBy === void 0) { sortBy = []; }
    ts_invariant_1.default(def, "execution definition to set sorts in must be defined");
    return tslib_1.__assign(tslib_1.__assign({}, def), { sortBy: sortBy });
}
exports.defSetSorts = defSetSorts;
/**
 * Creates new execution definition by merging new exection configuration into an existing definition.
 *
 * @param def - existing definition
 * @param config - execution configuration
 * @returns always new instance
 * @public
 */
function defSetExecConfig(def, config) {
    ts_invariant_1.default(def, "execution definition to set execution config in must be defined");
    return tslib_1.__assign(tslib_1.__assign({}, def), { executionConfig: config });
}
exports.defSetExecConfig = defSetExecConfig;
/**
 * Creates new execution definition by setting a new post processing.
 *
 * @param def - existing definition
 * @param postProcessing - configuration that should be done with the data after they are obtained from the server
 *  and before they are passed to the user
 * @returns always new instance
 * @public
 */
function defSetPostProcessing(def, postProcessing) {
    ts_invariant_1.default(def, "execution definition to set post processing in must be defined");
    return tslib_1.__assign(tslib_1.__assign({}, def), { postProcessing: postProcessing });
}
exports.defSetPostProcessing = defSetPostProcessing;
/**
 * Gets totals from particular dimension in the provided execution definition.
 *
 * @param def - definition to get totals from
 * @param dimIdx - dimension index
 * @returns empty list if no definition or dimension with the provided index not defined or if there are no
 *  totals in the dimension
 * @public
 */
function defTotals(def, dimIdx) {
    ts_invariant_1.default(def, "execution definition to get totals for must be defined");
    if (!def.dimensions[dimIdx]) {
        return [];
    }
    return dimension_1.dimensionTotals(def.dimensions[dimIdx]);
}
exports.defTotals = defTotals;
/**
 * Creates new execution definition by slapping the provided dimensions on top of the definition.
 *
 * @param def - existing definition
 * @param dimensions - dimensions
 * @returns always new instance
 * @public
 */
function defSetDimensions(def, dimensions) {
    if (dimensions === void 0) { dimensions = []; }
    ts_invariant_1.default(def, "execution definition to set dimension for must be defined");
    return tslib_1.__assign(tslib_1.__assign({}, def), { dimensions: dimensions });
}
exports.defSetDimensions = defSetDimensions;
/**
 * Calculates fingerprint for the execution definition. Fingerprinting is used as an _approximate_,
 * quick, first-level assessment whether two execution definitions are or are not effectively same = they
 * lead to the same computation on the backend.
 *
 * The contract and the approximate nature of the fingerprint can be described as follows:
 *
 * -  If two execution definitions have the same fingerprint, then they definitely are effectively the same
 *    from the result calculation point of view and the backend will perform the same computation for them.
 *
 * -  If two execution definition have different fingerprint, they MAY OR MAY NOT lead to different execution. Or
 *    more concrete: two executions with two different fingerprints MAY lead to the same execution and same results.
 *
 * While not optimal, this contract allows for safe usage of fingerprints to determine whether two
 * execution definitions have changed. For instance it can be used in React lifecycle methods (shouldComponentUpdate)
 * or for client-side caching.
 *
 * @param def - execution definition
 * @public
 */
function defFingerprint(def) {
    var _a, _b;
    ts_invariant_1.default(def, "execution definition to calculate fingerprint for must be defined");
    var hasher = new spark_md5_1.default();
    /*
     * Simple approach to construct exec definition fingerprint; the main drawback is that it completely
     * disregards that ordering of some array elements does not impact the results of the actual execution.
     *
     * - attributes, measures, filters, sortby and totals should be sorted first and then fingerprinted.
     * - dimensions must be fingerprinted in the defined order
     *
     * This simple approach can lead to 'false negatives' => code says executions are different while in
     * fact are the same. This does not lead to functional issues as the bear can deal with that and will
     * reuse cached and all. The only drawback is frontend cache misses.
     */
    var hashFun = hasher.append.bind(hasher);
    hasher.append(def.workspace);
    def.attributes.map(fingerprints_1.attributeFingerprint).forEach(hashFun);
    def.measures.map(fingerprint_1.measureFingerprint).forEach(hashFun);
    def.filters.map(fingerprint_3.filterFingerprint).filter(isString_1.default).forEach(hashFun);
    def.sortBy.map(fingerprints_1.sortFingerprint).forEach(hashFun);
    def.dimensions.map(fingerprint_2.dimensionFingerprint).forEach(hashFun);
    if ((_a = def.executionConfig) === null || _a === void 0 ? void 0 : _a.dataSamplingPercentage) {
        hashFun(fingerprints_1.dataSamplingFingerprint((_b = def.executionConfig) === null || _b === void 0 ? void 0 : _b.dataSamplingPercentage));
    }
    return hasher.end();
}
exports.defFingerprint = defFingerprint;
//# sourceMappingURL=index.js.map